import { validateEmail } from '@/blocks/forms/validation';
import { FormSendingResult } from './FormSendingResult';
import styles from './styles.module.scss';
import { SuccessIcon } from './FormSendingResult/FormSuccessIcon';
import { Checkbox, FormControlLabel } from '@/blocks/components/Controls/Checkbox';
import { TextField } from '@/blocks/components/Controls/TextField';
import { DocumentLink, LEGAL_DOCS } from '@/blocks/components/DocumentLink';
import { AccentFragment } from '@/blocks/components/HeadingAccentFragment';
import { Dialog, DialogHeadingWithCloseIcon } from '@/blocks/components/Modal/Dialog';
import { LinkSignin } from '@/blocks/components/PlatformLink';
import buttons from '@/styles/button.module.scss';
import typography from '@/styles/typography.module.scss';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import React, { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useTranslations } from 'next-intl';

type SignupForAssetFormData = {
  email: string;
  accept: boolean;
};

enum FormSendingStatus {
  ERROR,
  SUCCESS,
}

export function SignupForAssetDialog({
  opened,
  ticker,
  close,
}: {
  opened: boolean;
  ticker: string;
  close: () => void;
}) {
  const tr = useTranslations('pages.priceCharts.asset.signUp');
  const [sendingStatus, setSendingStatus] = useState<FormSendingStatus | null>(null);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(tr.rich('inputEmail.required') as string)
      .test('email', tr.rich('inputEmail.invalid') as string, validateEmail),
    accept: Yup.boolean()
      .required()
      .test((val) => val),
  });

  const { control, handleSubmit, reset, setError, formState, watch } = useForm<SignupForAssetFormData>({
    mode: 'onTouched',
    resolver: yupResolver(validationSchema),
    shouldFocusError: false,
    shouldUseNativeValidation: false,
    defaultValues: {
      email: '',
      accept: false,
    },
  });

  const onSubmit: SubmitHandler<SignupForAssetFormData> = async (data, event) => {
    // example in complaints page form
    // TODO
    setSendingStatus(FormSendingStatus.SUCCESS);
  };

  return (
    <Dialog open={opened} onClose={close} className={styles.dialog}>
      <DialogHeadingWithCloseIcon onClose={close}></DialogHeadingWithCloseIcon>
      {sendingStatus === null ? (
        <form action={'/'} onSubmit={handleSubmit(onSubmit)} className={styles.dialogInner}>
          <div className={styles.dialogHeadingBlock}>
            <div className={typography.h4}>{tr.rich('title', { ticker })}</div>
            <div className={typography.subheadingSM}>{tr.rich('subhead', { ticker })}</div>
          </div>
          <div className={styles.dialogFieldsBlock}>
            <Controller
              name={'email'}
              control={control}
              render={({ field: { ref, ...props }, fieldState: { error } }) => (
                <TextField
                  label={tr.rich('inputEmail.label')}
                  required
                  className={styles.commonInputStyles}
                  inputRef={ref}
                  error={Boolean(error)}
                  helperText={error?.message}
                  {...props}
                />
              )}
            />
            <Controller
              name={'accept'}
              control={control}
              render={({ field: { ref, ...props }, fieldState: { error } }) => (
                <FormControlLabel
                  label={tr.rich('checkbox.label', {
                    a1: (content) => (
                      <DocumentLink doc={LEGAL_DOCS.LEGAL_TERMS}>
                        <AccentFragment>{content}</AccentFragment>
                      </DocumentLink>
                    ),
                    a2: (content) => (
                      <DocumentLink doc={LEGAL_DOCS.PRIVACY}>
                        <AccentFragment>{content}</AccentFragment>
                      </DocumentLink>
                    ),
                  })}
                  className={styles.label}
                  control={<Checkbox {...props} />}
                  inputRef={ref}
                />
              )}
            ></Controller>
          </div>
          <div className={styles.dialogCtaBlock}>
            <button
              className={classNames(buttons.btn, buttons.btnPrimary, buttons.btnM)}
              disabled={!formState.isValid}
            >
              {tr.rich('actionButton')}
            </button>
            <div className={styles.footnote}>
              {tr.rich('footnote', {
                a: (content) => (
                  <LinkSignin>
                    <AccentFragment>{content}</AccentFragment>
                  </LinkSignin>
                ),
              })}
            </div>
          </div>
        </form>
      ) : sendingStatus === FormSendingStatus.SUCCESS ? (
        <FormSendingResult
          title={tr.rich('success.title')}
          subtitle={tr.rich('success.subhead')}
          onClose={close}
          cta={tr.rich('success.actionButton')}
          icon={<SuccessIcon />}
        />
      ) : (
        <FormSendingResult
          title={tr.rich('error.title')}
          subtitle={tr.rich('error.subhead')}
          onClose={close}
          cta={tr.rich('error.actionButton')}
          icon={<SuccessIcon />}
        />
      )}
    </Dialog>
  );
}
